/**
 * The agent's state, as the UI sees it.
 *
 * Reads once and then **subscribes**. Polling would be the obvious shape and it
 * is the wrong one: the agent can die at any moment and the port it comes back
 * on is new, so the window between the death and the next poll is a window in
 * which the UI is confidently wrong. `agent.onStateChange` pushes, so the gap is
 * a render rather than an interval.
 *
 * This hook is for *display*. Requests do not read it — `agentClient` resolves
 * the base URL itself, per call, because a value that has been through React
 * state is by definition a value from the last render.
 */

import { useEffect, useState } from "react";
import { agentTarget, probeBridge, type AgentTarget, type BridgeProbe } from "../lib/agentClient";

export function useAgent(): AgentTarget {
  const [probe, setProbe] = useState<BridgeProbe>({ kind: "resolving" });

  useEffect(() => {
    let cancelled = false;

    void probeBridge().then((next) => {
      // A push may have landed while the initial read was in flight; it is the
      // fresher of the two, so do not let this overwrite it on arrival.
      if (!cancelled) setProbe((current) => (current.kind === "resolving" ? next : current));
    });

    const bridge = window.bimgo?.agent;
    const unsubscribe = bridge?.onStateChange?.((state) => setProbe({ kind: "state", state }));

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return agentTarget(probe);
}

